import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { RefreshCw, Link2 } from 'lucide-react';
import { formatDate, formatCurrency } from '../../utils/formatters';
import {
  useBankIncoming,
  useMatchBankIncoming,
} from '../../hooks/react-query/useGeneralLedger';
import { useApiQuery } from '../../hooks/react-query/useApiQuery';
import { fundTransactionService } from '../../services/fundTransactionService';
import {
  BANK_TRANSACTION_TYPE_LABELS,
  type BankIncomingLine,
} from '@/types/bank-reconciliation';
import type {
  FundTransactionSummary,
  FundTransactionType,
} from '@/types/fund-transactions';

const CREDIT_TYPES: FundTransactionType[] = ['DRAWDOWN'];
const DEBIT_TYPES: FundTransactionType[] = [
  'DISTRIBUTION',
  'MANAGEMENT_FEE',
  'FUND_COST',
  'DEAL_COST',
];

const toCents = (value: number) => Math.round(value * 100);

interface BankIncomingSectionProps {
  fundId?: string;
}

export function BankIncomingSection({ fundId }: BankIncomingSectionProps) {
  const incomingQuery = useBankIncoming(fundId);
  const [selected, setSelected] = useState<BankIncomingLine | null>(null);

  const lines = incomingQuery.data ?? [];

  if (incomingQuery.isLoading) {
    return (
      <div className="flex items-center py-4 text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
        Loading bank incoming...
      </div>
    );
  }

  if (lines.length === 0) {
    return null; // Nothing awaiting a match - keep the tab focused on the ledger.
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Bank incoming
          </h3>
          <p className="text-sm text-muted-foreground">
            Bank lines awaiting a match. Match one to a fund transaction to
            settle it.
          </p>
        </div>
        <Badge variant="warning">{lines.length} awaiting match</Badge>
      </div>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">DATE</TableHead>
              <TableHead className="w-[200px]">COUNTERPARTY</TableHead>
              <TableHead>DESCRIPTION</TableHead>
              <TableHead className="w-[170px]">TYPE</TableHead>
              <TableHead className="text-right w-[160px]">AMOUNT</TableHead>
              <TableHead className="w-[120px]">STATUS</TableHead>
              <TableHead className="w-[120px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell className="align-top">
                  {formatDate(line.value_date ?? line.execution_date)}
                </TableCell>
                <TableCell className="align-top">
                  {line.counterparty_name || line.counterparty_reference || '-'}
                </TableCell>
                <TableCell className="align-top">
                  {line.remittance_information || '-'}
                </TableCell>
                <TableCell className="align-top text-muted-foreground">
                  {BANK_TRANSACTION_TYPE_LABELS[line.bank_transaction_type]}
                </TableCell>
                <TableCell className="text-right font-medium align-top">
                  <span
                    className={
                      line.amount < 0 ? 'text-red-600' : 'text-green-700'
                    }
                  >
                    {formatCurrency(line.amount, {
                      currency: line.currency ?? undefined,
                    })}
                  </span>
                </TableCell>
                <TableCell className="align-top">
                  <Badge variant="outline">Unmatched</Badge>
                </TableCell>
                <TableCell className="align-top text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelected(line)}
                  >
                    <Link2 className="h-4 w-4 mr-1" />
                    Match
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selected && fundId && (
        <MatchIncomingDialog
          fundId={fundId}
          line={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

interface MatchCandidatesContentProps {
  isLoading: boolean;
  candidates: FundTransactionSummary[];
  choiceId: string | null;
  onChoose: (id: string) => void;
}

function MatchCandidatesContent({
  isLoading,
  candidates,
  choiceId,
  onChoose,
}: MatchCandidatesContentProps) {
  if (isLoading) {
    return (
      <div className="flex items-center py-6 text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
        Loading transactions...
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="py-6 text-center text-muted-foreground">
        No open fund transaction is available for this line to settle.
      </div>
    );
  }

  return (
    <div className="border rounded-lg max-h-80 overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]" />
            <TableHead className="w-[120px]">DATE</TableHead>
            <TableHead>TYPE</TableHead>
            <TableHead className="text-right">AMOUNT</TableHead>
            <TableHead className="w-[100px]">STATUS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {candidates.map((tx) => (
            <TableRow
              key={tx.id}
              className="cursor-pointer"
              onClick={() => onChoose(tx.id)}
            >
              <TableCell>
                <input
                  type="radio"
                  name="match-candidate"
                  checked={choiceId === tx.id}
                  onChange={() => onChoose(tx.id)}
                />
              </TableCell>
              <TableCell>{formatDate(tx.transaction_date)}</TableCell>
              <TableCell>{tx.transaction_type}</TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(tx.amount, { currency: tx.currency })}
              </TableCell>
              <TableCell>
                <Badge variant="outline">{tx.status}</Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface MatchIncomingDialogProps {
  fundId: string;
  line: BankIncomingLine;
  onClose: () => void;
}

function MatchIncomingDialog({
  fundId,
  line,
  onClose,
}: MatchIncomingDialogProps) {
  const [choiceId, setChoiceId] = useState<string | null>(null);
  const matchMutation = useMatchBankIncoming(fundId);

  // Open cash transactions of the fund; filtered client-side to those this line
  // can settle (same direction, and the line fits within the transaction
  // amount). The backend re-validates against the exact outstanding balance.
  const txQuery = useApiQuery(
    ['fund-transactions', 'match-candidates', fundId],
    () => fundTransactionService.list(fundId, { page_size: 200 }),
    { enabled: true, staleTime: 30 * 1000 }
  );

  const candidates = useMemo(() => {
    const items: FundTransactionSummary[] = txQuery.data?.items ?? [];
    const isCredit = line.amount > 0;
    const allowedTypes = isCredit ? CREDIT_TYPES : DEBIT_TYPES;
    const target = toCents(Math.abs(line.amount));
    return items.filter(
      (tx) =>
        (tx.status === 'DRAFT' || tx.status === 'ISSUED') &&
        allowedTypes.includes(tx.transaction_type) &&
        toCents(tx.amount) >= target
    );
  }, [txQuery.data, line.amount]);

  const handleConfirm = () => {
    if (!choiceId) return;
    matchMutation.mutate(
      { incomingId: line.id, fundTransactionId: choiceId },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Match bank line to a fund transaction</DialogTitle>
          <DialogDescription>
            {formatCurrency(line.amount, {
              currency: line.currency ?? undefined,
            })}
            {line.counterparty_name ? ` from ${line.counterparty_name}` : ''} -
            pick the fund transaction it settles. Only open transactions of the
            same direction that this line can settle (fully or in part) are
            shown.
          </DialogDescription>
        </DialogHeader>

        <MatchCandidatesContent
          isLoading={txQuery.isLoading}
          candidates={candidates}
          choiceId={choiceId}
          onChoose={setChoiceId}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!choiceId || matchMutation.isPending}
          >
            {matchMutation.isPending ? 'Matching...' : 'Confirm match'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
